import { ALL_GAMES, type Game } from "./games";
import { drawsOnDate, hasDrawOccurred } from "@/lib/draw-schedule";

export interface LotteryResult {
  id: string;
  game_id: string;
  draw_date: string;
  winning_numbers: number[];
  bonus_numbers: number[];
  multiplier: number | null;
  games?: Game & { states?: any };
}

// Deterministic pseudo-random based on a seed string
function seededRand(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickNumbers(rand: () => number, count: number, range: number): number[] {
  const pool = Array.from({ length: range }, (_, i) => i + 1);
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(rand() * pool.length);
    out.push(pool.splice(idx, 1)[0]);
  }
  return out.sort((a, b) => a - b);
}

export function generateResultsForGame(g: Game, count: number, baseDate = new Date()): LotteryResult[] {
  const out: LotteryResult[] = [];
  // UTC-only date math: local-time helpers (setHours/setDate) shift the ISO day
  // for visitors east/west of UTC, which made "today" resolve to a date with no
  // generated draws and 404 the results pages during client-side navigation.
  const todayUtc = Date.parse(`${getSafeTodayIso(baseDate)}T00:00:00Z`);
  for (let i = 0; i < count; i++) {
    const isoDate = new Date(todayUtc - i * 86400000).toISOString().slice(0, 10);
    // Only generate draws on days the game actually runs, and never before the
    // scheduled draw time has passed for that day.
    if (!drawsOnDate(g.draw_schedule, isoDate)) continue;
    if (!hasDrawOccurred(g.draw_schedule, isoDate, baseDate)) continue;
    const rand = seededRand(`${g.id}-${isoDate}`);
    const winning = pickNumbers(rand, g.number_count_main, g.number_range_main);
    const bonus =
      g.number_count_bonus > 0 ? pickNumbers(rand, g.number_count_bonus, g.number_range_bonus) : [];
    const multiplier = g.has_multiplier ? [2, 2, 3, 3, 4, 5, 10][Math.floor(rand() * 7)] : null;
    out.push({
      id: `r-${g.id}-${isoDate}`,
      game_id: g.id,
      draw_date: isoDate,
      winning_numbers: winning,
      bonus_numbers: bonus,
      multiplier,
      games: g as any,
    });
  }
  return out;
}

let _ALL_RESULTS: LotteryResult[] | null = null;

/** Most recent already-drawn result for a game, or null if it hasn't drawn recently. */
export function generateLatestResultForGame(g: Game, baseDate = new Date()): LotteryResult | null {
  return generateResultsForGame(g, 21, baseDate)[0] ?? null;
}

let _ALL_RESULTS_BASE_DATE: string | null = null;

export function isSaneResultDate(date: string): boolean {
  if (!/^20\d{2}-\d{2}-\d{2}$/.test(date) || date < "2020-01-01") return false;
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date;
}

/** True only when the date is valid, not future-dated, and has generated draw data. */
export function isAvailableResultDate(date: string, baseDate = new Date()): boolean {
  return isSaneResultDate(date) && date <= getSafeTodayIso(baseDate) && getResultsByDate(date).length > 0;
}

export function getSafeTodayIso(baseDate = new Date()): string {
  const iso = new Date(baseDate).toISOString().slice(0, 10);
  return isSaneResultDate(iso) ? iso : "2026-07-09";
}

function clockFor(baseDate: Date): Date {
  const safeBase = new Date(`${getSafeTodayIso(baseDate)}T00:00:00Z`);
  // Keep the real clock (not midnight) so "has the draw happened yet" is accurate,
  // while the day window stays anchored to the safe ISO date.
  return getSafeTodayIso(baseDate) === new Date(baseDate).toISOString().slice(0, 10)
    ? new Date(baseDate)
    : new Date(safeBase.getTime() + 23 * 3600000);
}

// Draws before today never change, so they're generated once per day instead of
// on every minute bucket (~350ms of RNG work that used to land on TTFB).
let _HISTORY: LotteryResult[] | null = null;
let _HISTORY_DAY: string | null = null;

function getHistoryResults(baseDate: Date): LotteryResult[] {
  const today = getSafeTodayIso(baseDate);
  if (_HISTORY && _HISTORY_DAY === today) return _HISTORY;
  // Anchor to today midday UTC (so late-night ET draws from yesterday count as
  // occurred), then drop today's own draws — those are recomputed per minute.
  const noon = new Date(Date.parse(`${today}T12:00:00Z`));
  _HISTORY = ALL_GAMES.flatMap((g) =>
    generateResultsForGame(g, 121, noon).filter((r) => r.draw_date < today),
  );
  _HISTORY_DAY = today;
  return _HISTORY;
}

function computeAllResults(baseDate = new Date()): LotteryResult[] {
  const clock = clockFor(baseDate);
  const today = getSafeTodayIso(baseDate);
  const todays: LotteryResult[] = [];
  for (const g of ALL_GAMES) {
    if (!drawsOnDate(g.draw_schedule, today)) continue;
    if (!hasDrawOccurred(g.draw_schedule, today, clock)) continue;
    const [r] = generateResultsForGame(g, 1, clock);
    if (r) todays.push(r);
  }
  return [...todays, ...getHistoryResults(baseDate)];
}

interface ResultsIndex {
  all: LotteryResult[];
  byGame: Map<string, LotteryResult[]>;
  byDate: Map<string, LotteryResult[]>;
  latestByGame: Map<string, LotteryResult>;
  dates: string[];
}

let _INDEX: ResultsIndex | null = null;

function buildIndex(all: LotteryResult[]): ResultsIndex {
  const byGame = new Map<string, LotteryResult[]>();
  const byDate = new Map<string, LotteryResult[]>();
  const latestByGame = new Map<string, LotteryResult>();
  for (const r of all) {
    (byGame.get(r.game_id) ?? byGame.set(r.game_id, []).get(r.game_id)!).push(r);
    (byDate.get(r.draw_date) ?? byDate.set(r.draw_date, []).get(r.draw_date)!).push(r);
    const cur = latestByGame.get(r.game_id);
    if (!cur || r.draw_date > cur.draw_date) latestByGame.set(r.game_id, r);
  }
  for (const list of byGame.values()) list.sort((a, b) => b.draw_date.localeCompare(a.draw_date));
  const dates = Array.from(byDate.keys()).sort((a, b) => b.localeCompare(a));
  return { all, byGame, byDate, latestByGame, dates };
}

/** Minute-level cache key: today's list grows as draws happen. */
function currentBucket(baseDate = new Date()): string {
  const d = new Date(baseDate);
  return `${getSafeTodayIso(baseDate)}T${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

function getIndex(baseDate = new Date()): ResultsIndex {
  const bucket = currentBucket(baseDate);
  if (!_ALL_RESULTS || !_INDEX || _ALL_RESULTS_BASE_DATE !== bucket) {
    _ALL_RESULTS = computeAllResults(baseDate);
    _INDEX = buildIndex(_ALL_RESULTS);
    _ALL_RESULTS_BASE_DATE = bucket;
  }
  return _INDEX;
}

export function getAllResults(baseDate = new Date()): LotteryResult[] {
  return getIndex(baseDate).all;
}
// Lazy proxy so top-level access during Worker module init (where `new Date()`
// returns the Unix epoch) doesn't freeze results at 1970-01-01. First access
// happens inside a request, when real time is available.
export const ALL_RESULTS: LotteryResult[] = new Proxy([] as LotteryResult[], {
  get(_t, prop, recv) {
    return Reflect.get(getAllResults(), prop, recv);
  },
  has(_t, prop) {
    return Reflect.has(getAllResults(), prop);
  },
  ownKeys() {
    return Reflect.ownKeys(getAllResults());
  },
  getOwnPropertyDescriptor(_t, prop) {
    return Reflect.getOwnPropertyDescriptor(getAllResults(), prop);
  },
}) as LotteryResult[];

// Per-game memo: a game/history page only needs one game's draws, so it should
// not pay for generating all ~330 games (~350ms of work, notably on the client).
const _BY_GAME = new Map<string, LotteryResult[]>();
let _BY_GAME_BUCKET: string | null = null;

export function getResultsForGame(gameId: string, limit = 25) {
  if (_INDEX && _ALL_RESULTS_BASE_DATE === currentBucket()) {
    return (_INDEX.byGame.get(gameId) ?? []).slice(0, limit);
  }
  const bucket = currentBucket();
  if (_BY_GAME_BUCKET !== bucket) {
    _BY_GAME.clear();
    _BY_GAME_BUCKET = bucket;
  }
  let list = _BY_GAME.get(gameId);
  if (!list) {
    const game = ALL_GAMES.find((g) => g.id === gameId);
    list = game ? generateResultsForGame(game, 121, new Date()) : [];
    _BY_GAME.set(gameId, list);
  }
  return list.slice(0, limit);
}

export function getLatestResults(limit = 20) {
  // One row per game (its most recent draw), so games that don't draw daily
  // still surface their last numbers instead of being pushed out by daily games.
  return Array.from(getIndex().latestByGame.values())
    .sort((a, b) => b.draw_date.localeCompare(a.draw_date))
    .slice(0, limit);
}

export function getResultsByDate(date: string) {
  return getIndex().byDate.get(date) ?? [];
}

export function getAvailableResultDates(limit = 60) {
  return getIndex().dates.slice(0, limit);
}